import React, { useState, useMemo } from "react"
import { useProjectDocumentsQuery } from "features/Documents/api/useDocumentsQuery"
import { useCreateDocumentFolderMutation } from "features/Documents/api/useDocumentsMutations"

import { useProjectStore } from "stores/projectStore"

import DocumentTable from "features/Documents/components/DocumentTable/DocumentTable.component"
import DocumentsHeader from "features/Documents/components/DocumentsHeader.component"

import type { DocumentItem, FileItem, FolderItem } from "types/document.types"

/**
 * Container component for the documents section with folder structure support.
 *
 * This component includes a header with a search functionality and a table displaying documents in a folder hierarchy.
 *
 * @returns {JSX.Element} The rendered container component.
 */
const DocumentsContainer: React.FC = () => {
  const project = useProjectStore((state) => state.project)
  const [filter, setFilter] = useState<string>("")
  const [currentPath, setCurrentPath] = useState<string[]>([]) // Track current folder path

  // Filter/folder are part of the query key — search and navigation refetch
  // automatically; document mutations invalidate the list.
  const documentsQuery = useProjectDocumentsQuery(
    project.id,
    project.Created_by,
    filter,
    currentPath.join("/")
  )

  const createFolderMutation = useCreateDocumentFolderMutation()

  /**
   * Converts the API response to the frontend DocumentItem format
   */
  const folderStructure = useMemo((): DocumentItem[] => {
    const documents = documentsQuery.data ?? []
    return documents.map((item): DocumentItem => {
      if (item.is_folder) {
        const folderItem: FolderItem = {
          name: item.name,
          type: "folder",
          fullPath: item.full_path,
        }
        return folderItem
      } else {
        const fileItem: FileItem = {
          name: item.name,
          type: "file",
          fullPath: item.full_path,
        }
        return fileItem
      }
    })
  }, [documentsQuery.data])

  /**
   * Handles the search action.
   *
   * @param value - The search input value.
   */
  const onSearch = (value: string): void => {
    setFilter(value)
    // Reset to root when searching
    if (value.trim() !== "") {
      setCurrentPath([])
    }
  }

  /**
   * Navigates into a folder
   */
  const navigateToFolder = (folderName: string): void => {
    setCurrentPath((prev) => [...prev, folderName])
  }

  /**
   * Navigates back to a specific path level
   */
  const navigateToPath = (pathIndex: number): void => {
    setCurrentPath((prev) => prev.slice(0, pathIndex + 1))
  }

  /**
   * Navigates back to root
   */
  const navigateToRoot = (): void => {
    setCurrentPath([])
  }

  /**
   * Creates a new folder in the current directory. Rejections propagate to
   * the caller (DocumentsHeader), which owns the success/error messages.
   */
  const handleCreateFolder = async (folderName: string): Promise<void> => {
    const currentFolder = currentPath.join("/")
    const newFolderPath = currentFolder
      ? `${currentFolder}/${folderName}`
      : folderName

    await createFolderMutation.mutateAsync({
      projectId: project.id,
      owner: project.Created_by,
      folderPath: newFolderPath,
    })
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto w-full">
      <DocumentsHeader
        refresh={() => void documentsQuery.refetch()}
        project={project}
        onSearch={onSearch}
        currentPath={currentPath}
        navigateToPath={navigateToPath}
        navigateToRoot={navigateToRoot}
        onCreateFolder={handleCreateFolder}
        isCreatingFolder={createFolderMutation.isPending}
      />
      <div className="p-2">
        <DocumentTable
          project={project}
          isLoading={documentsQuery.isFetching}
          documents={folderStructure}
          onFolderClick={navigateToFolder}
        />
      </div>
    </div>
  )
}

export default DocumentsContainer
